<template>
  <GridContainer class="dcb-teaser-aktion">
    <Card v-bind="card">
      <template #default>
        <template v-if="step === 'inactive'">
          Bereits erfolgte Registrierungen werden berücksichtigt. Sofern innerhalb des
          Aktionszeitraums eine Zahlung per Handyrechnung getätigt wurde, erhältst du innerhalb von
          wenigen Tagen deine 5 € Gutschrift gemäß Teilnahmebedingungen<DcbFootnote /> <br /><br />
        </template>
        <template v-else-if="step === 'alreadyRegistered'">
          Deine Mobilfunknummer ist bereits bei uns registriert. Eine erneute Registrierung ist
          daher nicht möglich.<br /><br />Du kannst die Vorteile der Zahlung per Handyrechnung
          dennoch nutzen und diese bequem auf deinem Smartphone einrichten, um in vielen
          Partnershops unkompliziert mit deiner Handyrechnung zu bezahlen.
        </template>
        <template v-else-if="step === 'mailSent'">
          Du kannst jetzt 5 € Gutschrift gemäß Teilnahmebedingungen<DcbFootnote /> einsetzen.
        </template>
        <template v-else-if="step === 'retargeting'">
          So erhältst du deine Gutschrift für App- und In-App-Käufe. <DcbFootnote />
        </template>
        <template v-else></template>
      </template>
      <template #action>
        <template v-if="step === 'inactive'">
          <LinkButton
            tag="nuxt-link"
            to="zahlung-per-handyrechnung"
            data-tealium-rel="content.button.zahlung-per-handyrechnung"
            outline
          >
            Mehr zur Zahlmethode
          </LinkButton>
        </template>
        <template v-else-if="step === 'alreadyRegistered'">
          <LinkButton
            tag="button"
            type="button"
            primary
            @click="backToRegister"
          >
            Zurück zur Registrierung
          </LinkButton>
          <br /><br />
          <LinkButton
            tag="nuxt-link"
            to="zahlung-per-handyrechnung#apps"
            outline
          >
            Mehr Informationen
          </LinkButton>
        </template>
        <template v-else-if="step === 'mailSent'"> </template>
        <template v-else-if="step === 'retargeting'"> </template>
        <template v-else>
          <LinkButton
            primary
            tag="button"
            type="button"
            data-tealium-rel="content.button.teaser.scroll-to-form"
            @click="scrollToForm"
          >
            Jetzt Gutschrift sichern
          </LinkButton>
        </template>
      </template>
    </Card>
  </GridContainer>
</template>

<script setup lang="ts">
import { GridContainer, LinkButton, Card } from '@i22team/td-longtail-vue-components';
import type { CardComponentProps } from '@i22team/td-longtail-vue-components';
import DcbFootnote from '~/components/dcb/dcb-footnote.vue';
import type { DcbStep } from '~/lib/dcb/dcb-double-opt-in';

export interface DCBTeaserAktion {
  step: DcbStep;
}
const props = withDefaults(defineProps<DCBTeaserAktion>(), {});

const emit = defineEmits(['showForm']);

const title = computed(() => {
  switch (props.step) {
    case 'inactive':
      return 'Aktion beendet';
    case 'alreadyRegistered':
      return 'Du bist bereits registriert';
    case 'mailSent':
      return 'Registrierung erfolgreich!';
    case 'retargeting':
      return 'Zwei Schritte. Kein Stress.';
    default:
      return 'Bis zu 5&nbsp;€ Gutschrift sichern';
  }
});

const card = computed(() => {
  const cardElement: CardComponentProps = {
    label: {
      customClass: 'text--bold text--h4',
      //   text: 'Winter-Special',
    },
    heading: {
      tag: 'h1',
      size: 'lg',
      text: title.value,
      spacesBottomRem: true,
    },
    imagePosition: 'right',
    imagePositionXs: 'top',
    image: {
      src: '/content/content-images/dcb/maerz/469af80c3e97f9e0ff8eaac8ae6fe201/5-eur-Kampagne_V2.png',
      aspectRatio: '617 / 544',
      sizes: 'xs:150vw sm:75vw md:50vw lg:50vw',
      alt: 'Ein Kassenzettel mit 5 € Gutschrift',
    },
    imageBackground: {
      imageStyle: '--card-image-background-color: #FFF',
    },
  };
  if (props.step === 'mailSent' && cardElement.label) {
    cardElement.label.text = 'Bis zu 5&nbsp;€ Gutschrift';
  }
  if (props.step === 'inactive') {
    cardElement.badge = {
      text: 'Gewinnspiel beendet',
    };
  }
  return cardElement;
});

const scrollToForm = () => {
  document.getElementById('dcb-mailform')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const backToRegister = () => {
  emit('showForm', true);
  setTimeout(scrollToForm, 1);
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.dcb-teaser-aktion {
  margin-bottom: $grid-base * 3;

  .card {
    --card-container-justify: center;
    --card-container-content-flex: 0;
    --card-image-container-height: 300px;

    @include mq($mq-sm) {
      --card-image-container-height: 500px;
      height: 500px;
    }

    :deep(.card__action) {
      flex-wrap: wrap;
    }

    :deep(.card__image) {
      @include mq(false, $mq-sm - 1) {
        --card-image-padding: 1rem;
        --card-image-container-height: 150px;
      }
    }
  }
}
</style>
